import React from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAdd, faTrash } from "@fortawesome/free-solid-svg-icons";
import CustomAddButton from "@/common/components/custom/buttons/CustomAddButton";
import CustomDeleteButton from "@/common/components/custom/buttons/CustomDeleteButton";
import SelectInput from "@/common/components/custom/SelectInput";
import {
  GOOGLE_VOICES_DOCS_URL,
  VOICE_GENDER_OPTIONS,
  VOICE_TYPE_OPTIONS,
  createEmptyVoice,
} from "../constants/voice";
import { Color } from "@/common/colors/colors";

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 12px 0 8px;
`;

const HeaderTitleGroup = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
`;

const HeaderTitle = styled.h4`
  margin: 0;
`;

const DocsLink = styled.a`
  font-size: 13px;
  color: ${Color.blue};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const HeadCell = styled.th`
  padding: 8px;
  background-color: lightgrey;
  text-align: left;
  font-size: 13px;
`;

const Cell = styled.td`
  padding: 8px;
  border-bottom: 1px solid #e5e5e5;
  vertical-align: middle;
`;

const CellInput = styled.input`
  width: 140px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid lightgray;
  outline: none;
`;

const EmptyHint = styled.p`
  margin: 8px 0;
  color: #7f8087;
  font-size: 13px;
`;

const VoiceConfigTable = ({ voices = [], setVoices }) => {
  const handleAdd = () => {
    setVoices([...voices, createEmptyVoice()]);
  };

  const handleChange = (index, field, value) => {
    setVoices(
      voices.map((voice, voiceIndex) => {
        if (voiceIndex !== index) {
          return voice;
        }

        return {
          ...voice,
          [field]: value,
        };
      })
    );
  };

  const handleRemove = (index) => {
    setVoices(voices.filter((_, voiceIndex) => voiceIndex !== index));
  };

  return (
    <>
      <Header>
        <HeaderTitleGroup>
          <HeaderTitle>Voice configs</HeaderTitle>
          <DocsLink
            href={GOOGLE_VOICES_DOCS_URL}
            target="_blank"
            rel="noreferrer"
          >
            Google TTS voices
          </DocsLink>
        </HeaderTitleGroup>
        <CustomAddButton
          text={<FontAwesomeIcon icon={faAdd} />}
          onClick={handleAdd}
        />
      </Header>
      {voices.length === 0 && (
        <EmptyHint>Add a voice row, then pick the type.</EmptyHint>
      )}
      {voices.length > 0 && (
        <Table>
          <thead>
            <tr>
              <HeadCell>Type</HeadCell>
              <HeadCell>Language code</HeadCell>
              <HeadCell>Voice name</HeadCell>
              <HeadCell>Gender</HeadCell>
              <HeadCell>Encoding</HeadCell>
              <HeadCell></HeadCell>
            </tr>
          </thead>
          <tbody>
            {voices.map((voice, index) => (
              <tr key={`${voice.type}-${index}`}>
                <Cell>
                  <SelectInput
                    showLabel={false}
                    width={0}
                    selectWidth={140}
                    margin="0"
                    options={VOICE_TYPE_OPTIONS}
                    selected={
                      VOICE_TYPE_OPTIONS.find(
                        (option) => option.value === voice.type
                      ) || VOICE_TYPE_OPTIONS[0]
                    }
                    setSelected={(option) =>
                      handleChange(index, "type", option.value)
                    }
                  />
                </Cell>
                <Cell>
                  <CellInput
                    value={voice.languageCode || ""}
                    placeholder="el-GR"
                    onChange={(e) =>
                      handleChange(index, "languageCode", e.target.value)
                    }
                  />
                </Cell>
                <Cell>
                  <CellInput
                    value={voice.name || ""}
                    placeholder="el-GR-Standard-A"
                    onChange={(e) =>
                      handleChange(index, "name", e.target.value)
                    }
                  />
                </Cell>
                <Cell>
                  <SelectInput
                    showLabel={false}
                    width={0}
                    selectWidth={140}
                    margin="0"
                    options={VOICE_GENDER_OPTIONS}
                    selected={
                      VOICE_GENDER_OPTIONS.find(
                        (option) => option.value === voice.ssmlGender
                      ) || VOICE_GENDER_OPTIONS[0]
                    }
                    setSelected={(option) =>
                      handleChange(index, "ssmlGender", option.value)
                    }
                  />
                </Cell>
                <Cell>
                  <CellInput
                    value={voice.audioEncoding || "MP3"}
                    onChange={(e) =>
                      handleChange(index, "audioEncoding", e.target.value)
                    }
                  />
                </Cell>
                <Cell>
                  <CustomDeleteButton
                    text={<FontAwesomeIcon icon={faTrash} />}
                    onClick={() => handleRemove(index)}
                  />
                </Cell>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
};

export default VoiceConfigTable;
